import React, { useState } from 'react';
import { generateFlowMeditation } from '../services/geminiService';
import { FlowMeditation } from '../types';
import { Droplets, Wind, Waves, RefreshCw, Play } from 'lucide-react';
import { useUser } from '../context/UserContext';

const LettingGoFlow: React.FC = () => {
  const [feeling, setFeeling] = useState('');
  const [meditation, setMeditation] = useState<FlowMeditation | null>(null);
  const [loading, setLoading] = useState(false);
  const { addXp, unlockBadge } = useUser();

  const handleGenerate = async () => {
    if (!feeling.trim()) return;
    setLoading(true);
    setMeditation(null);
    try {
      const data = await generateFlowMeditation(feeling);
      setMeditation(data);
      addXp(30, 'Entered Flow State');
      unlockBadge('water_flow');
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto px-6 pb-20 animate-fadeIn">
      <header className="mb-10 text-center">
        <div className="inline-block p-3 bg-blue-50 text-blue-600 rounded-full mb-4">
          <Droplets size={32} />
        </div>
        <h1 className="text-3xl font-serif font-bold text-nunchi-900 mb-2">The Flow State</h1>
        <p className="text-nunchi-600">
          Resistance creates suffering. Acceptance creates flow. <br/>
          Find the element that balances your current emotion.
        </p>
      </header>

      {!meditation && !loading && (
        <div className="bg-white p-6 rounded-2xl shadow-sm border border-nunchi-200">
            <label className="block text-sm font-bold text-nunchi-500 uppercase tracking-wider mb-3">
                How are you feeling right now?
            </label>
            <input
              type="text"
              value={feeling}
              onChange={(e) => setFeeling(e.target.value)}
              placeholder="e.g., Stuck, Anxious, Overwhelmed, Rushed..."
              className="w-full bg-nunchi-50 rounded-xl p-4 border-none focus:ring-2 focus:ring-blue-200 outline-none text-nunchi-800 placeholder:text-nunchi-300 mb-4 font-medium"
            />
            <button
              onClick={handleGenerate}
              disabled={!feeling.trim()}
              className="w-full bg-blue-700 text-white py-4 rounded-xl font-bold hover:bg-blue-800 transition-all flex justify-center items-center gap-2 disabled:opacity-50"
            >
              <Waves size={18} /> Find My Flow
            </button>
        </div>
      )}

      {loading && (
        <div className="text-center py-20">
             <div className="flex justify-center gap-2 mb-4">
                 <div className="w-4 h-4 bg-blue-400 rounded-full animate-bounce delay-75"></div>
                 <div className="w-4 h-4 bg-blue-500 rounded-full animate-bounce delay-150"></div>
                 <div className="w-4 h-4 bg-blue-600 rounded-full animate-bounce delay-300"></div>
             </div>
             <p className="font-serif italic text-blue-700">Connecting to the current...</p>
        </div>
      )}

      {meditation && (
        <div className="animate-fadeIn space-y-6">
            <div className="bg-blue-900 text-blue-50 p-8 rounded-2xl shadow-xl relative overflow-hidden">
                 <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full blur-3xl translate-x-1/2 -translate-y-1/2"></div>
                 
                 <div className="text-center mb-8">
                     <span className="inline-block px-3 py-1 bg-blue-800 text-blue-200 text-xs font-bold uppercase tracking-widest rounded-full mb-3">
                         Element: {meditation.element}
                     </span>
                     <h2 className="text-3xl font-serif font-bold text-white">{meditation.title}</h2>
                 </div>

                 <div className="prose prose-lg text-blue-100 leading-relaxed font-serif italic border-l-4 border-blue-500 pl-6">
                     {meditation.visualization.split('\n').map((para, i) => (
                         <p key={i} className="mb-4">{para}</p>
                     ))}
                 </div>
            </div>

            <div className="text-center">
                <button 
                  onClick={() => { setMeditation(null); setFeeling(''); }}
                  className="text-blue-600 hover:text-blue-800 font-bold flex items-center gap-2 mx-auto"
                >
                    <RefreshCw size={16} /> Find Another Flow
                </button>
            </div>
        </div>
      )}
    </div>
  );
};

export default LettingGoFlow;